import Image from 'react-bootstrap/Image';

function KarateAzAgynak() {
    return (
        <div className="kb-article-body">
            <p>
                A köztudatban a sportot gyakran csak a fizikai erőnléttel, az izomépítéssel és a fogyással
                kapcsolják össze. A modern neurobiológia azonban bebizonyította, hogy a rendszeres fizikai
                aktivitás az egyik leghatékonyabb dolog, amit az agyunk egészségéért és kognitív (gondolkodási)
                képességeink fejlesztéséért tehetünk. Amikor edzünk, nemcsak az izmaink, hanem az idegsejtjeink
                is „formába lendülnek".
            </p>
            <p>
                A karate mozgás anyaga, mint például a <strong>kihon</strong> (alaptechnika) a <strong>kata</strong> (formagyakorlat) képzés, különlegesen összetett
                módon stimulálja az idegrendszert, mivel a fizikai terhelést magas szintű koordinációval,
                figyelemmel és mentális fókusszal ötvözi.
            </p>

            <section className="kb-section">
                <h2>1. Agyunk csodafegyvere: A BDNF fehérje</h2>
                <Image src="/pictures/tudasbazis/karate-az-agynak/Pict04.jpeg" fluid rounded className="kb-section-image" />
                <p>
                    A rendszeres mozgás egyik legfontosabb neurobiológiai hatása, hogy serkenti egy különleges
                    fehérje, a <strong>BDNF</strong> (<em>Brain-Derived Neurotrophic Factor</em> – agyi eredetű
                    neurotróf faktor) termelődését.
                </p>
                <ul className="kb-effect-list">
                    <li>
                        <strong>A mozgás hatása:</strong> A BDNF olyan az agynak, mint a trágya a növényeknek:
                        védi a meglévő idegsejteket, és ami a legfontosabb, serkenti az új idegsejtek
                        képződését (<strong>neurogenezis</strong>) és az új idegpályák (szinapszisok)
                        kialakulását. Ez a folyamat a <strong>neuroplaszticitás</strong>, az agy
                        alkalmazkodóképességének az alapja.
                    </li>
                    <li>
                        <strong>Miért fontos ez?</strong> Ez a folyamat elsősorban a{' '}
                        <strong>hippocampus</strong> nevű területen zajlik, amely a memória és a tanulás
                        központja. A mozgás tehát közvetlenül növeli a tanulási kapacitást és javítja a
                        memória működését.
                    </li>
                </ul>

                <div className="kb-table-wrap">
                    <table className="kb-table">
                        <thead>
                            <tr>
                                <th>Élettani terület</th>
                                <th>Utánpótlás korosztály (7–14 év)</th>
                                <th>Felnőttek és 50+ korosztály</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td data-label="Élettani terület">Magas BDNF szinttel</td>
                                <td data-label="Utánpótlás korosztály (7–14 év)">
                                    Gyorsabb információfeldolgozás, jobb iskolai teljesítmény, könnyebb
                                    nyelvtanulás.
                                </td>
                                <td data-label="Felnőttek és 50+ korosztály">
                                    Kognitív hanyatlás lassítása, demencia kockázatának csökkentése, szellemi
                                    frissesség.
                                </td>
                            </tr>
                            <tr>
                                <td data-label="Élettani terület">Alacsony BDNF szinttel</td>
                                <td data-label="Utánpótlás korosztály (7–14 év)">
                                    Tanulási nehézségek, figyelemzavar, gyengébb memória.
                                </td>
                                <td data-label="Felnőttek és 50+ korosztály">
                                    Meglassult gondolkodás, feledékenység, fokozott kockázat a
                                    neurodegeneratív betegségekre.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="kb-section">
                <h2>2. A „menedzser agy": Végrehajtó funkciók és fókusz</h2>
                <Image src="/pictures/tudasbazis/karate-az-agynak/Pict05.jpeg" fluid rounded className="kb-section-image" />
                <p>
                    A mozgás nemcsak a memóriát javítja, hanem élesíti azokat az összetett gondolkodási
                    folyamatokat is, amelyek a mindennapi életben, az iskolában vagy a munkahelyen
                    elengedhetetlenek. Ezeket összefoglaló néven <strong>végrehajtó funkcióknak</strong> (
                    <em>Executive Functions</em>) nevezzük.
                </p>
                <ul className="kb-effect-list">
                    <li>
                        <strong>Hogyan működik?</strong> A komplex, koordinációt és figyelmet igénylő
                        mozgásformák (mint a karate formagyakorlatok vagy a Kihon) edzik a{' '}
                        <strong>prefrontális kérget</strong>. Ez a terület felelős a fókuszált figyelemért, a
                        munkamemóriáért (az információ rövid távú észben tartásáért), a döntéshozatalért és az
                        impulzuskontrollért.
                    </li>
                    <li>
                        <strong>Szenzomotoros integráció:</strong> A karate edzésen a test és a fej gyors
                        pozícióváltásai stimulálják a <strong>vestibuláris rendszert</strong>
                        (egyensúlyérzékelést) és a <strong>kisagyat</strong> (<em>cerebellum</em>). A kisagy
                        nemcsak a mozgást koordinálja, hanem szoros kapcsolatban áll a prefrontális kéreggel,
                        támogatva a logikai gondolkodást és az információfeldolgozás sebességét.
                    </li>
                </ul>

                <div className="kb-table-wrap">
                    <table className="kb-table">
                        <thead>
                            <tr>
                                <th>Élettani terület</th>
                                <th>Utánpótlás korosztály (7–14 év)</th>
                                <th>Felnőttek és 50+ korosztály</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td data-label="Élettani terület">Edzett prefrontális kéreggel</td>
                                <td data-label="Utánpótlás korosztály (7–14 év)">
                                    Jobb koncentráció az órákon, kevesebb fegyelmi probléma, hatékonyabb
                                    időbeosztás.
                                </td>
                                <td data-label="Felnőttek és 50+ korosztály">
                                    Stressztűrés növekedése, jobb problémamegoldó képesség, hatékonyabb
                                    multitasking.
                                </td>
                            </tr>
                            <tr>
                                <td data-label="Élettani terület">Alulstimulált területekkel</td>
                                <td data-label="Utánpótlás korosztály (7–14 év)">
                                    Hiperaktivitás, figyelemzavar (ADHD-szerű tünetek), tanulási akadályok.
                                </td>
                                <td data-label="Felnőttek és 50+ korosztály">
                                    Meglassult döntéshozatal, mentális fáradékonyság, romló stresszkezelés.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="kb-section">
                <h2>3. Stresszkezelés és érzelmi egyensúly</h2>
                <Image src="/pictures/tudasbazis/karate-az-agynak/Pict06.jpeg" fluid rounded className="kb-section-image" />
                <p>
                    A mozgás közvetlen hatással van a hormonháztartásra, ami az érzelmi állapotunkat és a
                    stresszkezelő képességünket is meghatározza.
                </p>
                <ul className="kb-effect-list">
                    <li>
                        <strong>Hogyan hat?</strong> A fizikai aktivitás segít lebontani a stresszhormonokat (
                        <strong>kortizol</strong>), miközben stimulálja az <strong>endorfin</strong>{' '}
                        ("boldogsághormon"), <strong>dopamin</strong> (motiváció) és <strong>szerotonin</strong>{' '}
                        (hangulatszabályozás) termelését.
                    </li>
                    <li>
                        <strong>Hosszú távon:</strong> Ez a hormonális változás nemcsak a hangulatot javítja,
                        de növeli a pszichés rugalmasságot (<strong>reziliencia</strong>), csökkenti a
                        szorongást és javítja az alvásminőséget, ami elengedhetetlen az agy éjszakai
                        regenerációjához és a memória konszolidációjához (rögzüléséhez).
                    </li>
                </ul>

                <div className="kb-table-wrap">
                    <table className="kb-table">
                        <thead>
                            <tr>
                                <th>Hormonális egyensúly</th>
                                <th>Utánpótlás korosztály (7–14 év)</th>
                                <th>Felnőttek és 50+ korosztály</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td data-label="Hormonális egyensúly">Optimális szinttel</td>
                                <td data-label="Utánpótlás korosztály (7–14 év)">
                                    Kevesebb szorongás (pl. dolgozatok előtt), jobb érzelemszabályozás,
                                    nyugodtabb alvás.
                                </td>
                                <td data-label="Felnőttek és 50+ korosztály">
                                    Csökkenő munkahelyi stressz, jobb kedélyállapot, hatékonyabb relaxáció.
                                </td>
                            </tr>
                            <tr>
                                <td data-label="Hormonális egyensúly">Kortizol túlsúly esetén</td>
                                <td data-label="Utánpótlás korosztály (7–14 év)">
                                    Fokozott ingerlékenység, szorongás, alvászavarok, ami rontja a tanulást.
                                </td>
                                <td data-label="Felnőttek és 50+ korosztály">
                                    Krónikus stressz, kiégés kockázata, hangulatzavarok,
                                    koncentrációvesztés.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

export default KarateAzAgynak;
